import "../styleSheets/dashboardFiles.css";
import "../styleSheets/buttons.css";
import "../styleSheets/commonElements.css";
import React, {useEffect} from "react";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import { useUser } from "../contexts/UserContext";
import {
    useAttachments,
    useEffectivePlatformLimits,
    useUserPlan,
} from "../graphql/hooks";
import WidgetState from "../components/WidgetState";
import {TooltipButton} from "../utilities/ToolTipButton";
import { getAttachmentAddEligibility } from "../utilities/platformLimits";

function DashboardFiles() {
    const { user, isAuthenticated } = useUser();
    const navigate = useNavigate();
    const {t} = useTranslation("common");
    const userId = user?.userId ?? "";

    useEffect(() => {
        if(!isAuthenticated) {
            navigate("/", { state: { message: t("common.signIn") } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, navigate,]);

    const {
        data: attachments = [],
        isLoading,
        isError,
        error,
    } = useAttachments(userId, isAuthenticated && !!userId);

    const planQ = useUserPlan(userId, isAuthenticated && !!userId);
    const effectivePlatformLimitsQ = useEffectivePlatformLimits(
        userId,
        isAuthenticated && !!userId,
    );
    const userPlan = planQ.data;
    const attachmentCount = attachments.length;
    const attachmentAddEligibility = getAttachmentAddEligibility({
        attachmentsAllowed: userPlan?.attachmentsAllowed,
        currentAttachmentCount: attachmentCount,
        maxStorageBytesPerUser:
            effectivePlatformLimitsQ.data?.maxStorageBytesPerUser,
        storageUsedBytes: effectivePlatformLimitsQ.data?.storageUsedBytes,
        isPlanLoading: planQ.isLoading,
        isEffectivePlatformLimitsLoading: effectivePlatformLimitsQ.isLoading,
    });
    const attachmentAddTooltip =
        attachmentAddEligibility.reason === "loading"
            ? t("common.loading")
            : attachmentAddEligibility.reason === "plan_attachment_count_reached"
                ? t("file.errorMessage.planLimitReached")
                : attachmentAddEligibility.reason === "platform_storage_reached"
                    ? t("file.errorMessage.storageLimitReached")
                    : undefined;


    if (!isAuthenticated) {
        navigate("/", {state: {message: t("common.signIn")}});
    }

    function addAttachment() {
        navigate("/AttachmentEdit/", { state: null });
    }

    function seeAllAttachments() {
        navigate("/Panel?selectedTab=files");
    }

    return (
        <section className="section-files" aria-labelledby="files-heading">
            <section className={"files-header"}>
                <div className={"files-header-left"}>
                    <div className={"icon-container files-icon"}></div>
                    <h2 id="files-heading" className={"files-title"}>{t("file.myFiles")}</h2>
                </div>

                <div className={"files-count"}>
                    {isLoading
                        ? "Loading..."
                        : `${attachmentCount} ${attachmentCount === 1 ? t("file.file") : t("file.files")}`}
                </div>
            </section>

            <section className={"files-center"}>
                <WidgetState
                    isLoading={isLoading}
                    isError={isError}
                    error={error}
                    isEmpty={!isLoading && attachmentCount === 0}
                    emptyText={t("file.onScreenMessage.noFilesYet")}
                    className="files-empty"
                >
                    <>
                        <div className="files-count-container">
                            <div className="files-count-in-container">{attachmentCount}</div>
                            <div className="files-text-in-container">{t("file.active")}</div>
                        </div>
                        {//TODO decide if this display with active files whould be removed and replaced with a short message
                        }
                        {/*<div className="files-count-container">
                            <div className="files-count-in-container">{attachmentCount}</div>
                            <div className="files-text-in-container">{t("file.inActive")}</div>
                        </div>*/}
                    </>
                </WidgetState>
            </section>
            <section className={"files-bottom"}>
                <TooltipButton
                    label={"+ " + t("file.newFile")}
                    tooltip={attachmentAddTooltip}
                    disabled={!attachmentAddEligibility.canAddAttachment}
                    onClick={addAttachment}
                    buttonClassName="btn btn--orange btn--size-md"
                />
                {attachmentCount > 0 && (
                <button className={"btn btn--white btn--size-md"} onClick={seeAllAttachments}>
                    {t("common.seeAll")}
                </button>)}
            </section>
        </section>
    );
}

export default DashboardFiles;
